/**
 * NoteChip — on-demand designer note for the Design brief chip strip.
 *
 * Resting state signals whether a note exists (so it's never invisible):
 *   - empty  → dashed "Add Note" chip
 *   - filled → solid navy "Note •" chip
 * Clicking opens a popover with the note textarea. Autosaves on blur / on close
 * via PUT /orders/{id} (same `designer_notes` field the old panel used) — no
 * backend change. Desktop only; the mobile brief keeps DesignerNotesCard.
 */
import React, { useState } from 'react';
import { FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { isDesktopOffline } from '@/api/desktopSync';
import { useDesignerNote } from '@/hooks/useDesignerNote';
import DesignerNoteStatus from './DesignerNoteStatus';

export default function NoteChip({ order, onNoteSaved }) {
  const offline = isDesktopOffline();
  const note = useDesignerNote(order, { offline, onNoteSaved });
  const { value, setValue, save, readOnly, visible } = note;
  const [open, setOpen] = useState(false);

  const hasNote = (value || '').trim().length > 0;

  // Offline is read-only: a note can be read but not edited, and with no note
  // there's nothing to show (and nothing to add).
  if (!visible || (offline && !hasNote)) return null;

  return (
    <div className="inline-flex max-w-xs flex-col items-start gap-2">
      <Popover open={open} onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen && !offline) save();
      }}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={
              hasNote
                ? 'inline-flex items-center gap-1.5 rounded-full bg-slate-800 px-3 py-1 text-xs font-bold text-white hover:bg-slate-700 transition-colors'
                : 'inline-flex items-center gap-1.5 rounded-full border border-dashed border-stone-300 bg-white px-3 py-1 text-xs font-semibold text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-colors'
            }
            data-testid="brief-note-chip"
          >
            <FileText className="h-3.5 w-3.5" />
            {hasNote ? (
              <span className="inline-flex items-center gap-1.5">Note <span className="inline-block h-1.5 w-1.5 rounded-full bg-white/90" /></span>
            ) : (
              'Add Note'
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0" data-testid="brief-note-popover">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-stone-200">
            <span className="text-sm font-bold text-stone-800">Designer Note</span>
          </div>
          <div className="px-4 py-3">
            {offline ? (
              <p
                className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap"
                data-testid="brief-note-readonly"
              >
                {value}
              </p>
            ) : (
              <textarea
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={save}
                readOnly={readOnly}
                aria-label="Designer note"
                aria-busy={note.saving || note.checking}
                placeholder="Add a note about this order… (e.g. a substitution)"
                rows={4}
                autoFocus
                className="w-full text-sm text-stone-700 leading-relaxed resize-none border-0 focus:outline-none focus:ring-0 p-0 placeholder:text-stone-300 bg-transparent"
                data-testid="brief-note-textarea"
              />
            )}
            <div className="mt-2"><DesignerNoteStatus note={note} testId="brief-note" /></div>
          </div>
        </PopoverContent>
      </Popover>
      {!open && <DesignerNoteStatus note={note} testId="brief-note" />}
    </div>
  );
}
